import { useEffect, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuthState, useCausesPortalAnalytics } from '@percent/cause-dashboard/common/hooks'
import { PageViewSource } from '@percent/cause-dashboard/common/hooks/useCausesDashboardAnalytics/marketplaceAnalytics.types'
import { getSmartApplyPartnerApplyType, SmartApplyPartner } from '@percent/cause-dashboard/constants/smartApplyPartners'
import type { SmartApplyVariant } from '../useSmartApplyVariant'

/** Stored A/B variant or a tracking-only label for a standalone modal (e.g. 'second_smart_apply'). */
type TrackableSmartApplyVariant = SmartApplyVariant | 'second_smart_apply'

const locationToPageMap: Record<string, PageViewSource> = {
  '/home': PageViewSource.Home,
  '/product-offers': PageViewSource.AppStore
}

interface SmartApplyTrackingProps {
  variant: TrackableSmartApplyVariant
  page?: number | null
}

interface ProductTrackingData {
  productName: string
  error?: string
}

interface ModalApplyTrackingData {
  products: Record<string, boolean>
}

interface DismissedTrackingData {
  appliedProductsCount: number
}

export function useSmartApplyTracking({ variant, page }: SmartApplyTrackingProps) {
  const { authState } = useAuthState()
  const location = useLocation()
  const { track } = useCausesPortalAnalytics()
  const hasTracked = useRef(false)

  useEffect(() => {
    if (hasTracked.current) {
      return
    }

    const trackPopupShown = async () => {
      await track('Smart Apply Popup Shown', {
        organisation: authState.claimOrganisation?.displayName,
        organisationId: authState.claimOrganisation?.id,
        page: locationToPageMap[location.pathname] || location.pathname,
        accountId: authState.user?.id,
        shownOnHomepageModalPageNumber: page ?? null,
        experimentVariant: variant
      })
    }

    trackPopupShown()
    hasTracked.current = true
  }, [
    track,
    location.pathname,
    authState.claimOrganisation?.displayName,
    authState.claimOrganisation?.id,
    authState.user?.id,
    page,
    variant
  ])

  const trackProductApplied = useCallback(
    async ({ productName }: ProductTrackingData) => {
      await track('Smart Apply Product Applied', {
        accountId: authState.user?.id,
        organisationId: authState.claimOrganisation?.id,
        organisation: authState.claimOrganisation?.displayName,
        page: location.pathname,
        productName,
        applyType: getSmartApplyPartnerApplyType(productName as SmartApplyPartner),
        experimentVariant: variant
      })
    },
    [
      track,
      authState.user?.id,
      authState.claimOrganisation?.id,
      authState.claimOrganisation?.displayName,
      location.pathname,
      variant
    ]
  )

  const trackModalApplied = useCallback(
    async ({ products }: ModalApplyTrackingData) => {
      await track('Smart Apply Modal Applied', {
        accountId: authState.user?.id,
        organisationId: authState.claimOrganisation?.id,
        organisation: authState.claimOrganisation?.displayName,
        page: location.pathname,
        experimentVariant: variant,
        ...products
      })
    },
    [
      track,
      authState.user?.id,
      authState.claimOrganisation?.id,
      authState.claimOrganisation?.displayName,
      location.pathname,
      variant
    ]
  )

  const trackDismissed = useCallback(
    async ({ appliedProductsCount }: DismissedTrackingData) => {
      await track('Smart Apply Dismissed', {
        accountId: authState.user?.id,
        organisationId: authState.claimOrganisation?.id,
        organisation: authState.claimOrganisation?.displayName,
        page: location.pathname,
        experimentVariant: variant,
        appliedProductsCount
      })
    },
    [
      track,
      authState.user?.id,
      authState.claimOrganisation?.id,
      authState.claimOrganisation?.displayName,
      location.pathname,
      variant
    ]
  )

  return {
    trackProductApplied,
    trackModalApplied,
    trackDismissed
  }
}
